import React, { useMemo, useRef, useEffect, useState } from 'react';
import Chart from 'react-apexcharts';

const DipGalleryChart = ({ data, logoSrc }) => {
  const chartContainerRef = useRef(null);
  const [chartHeight, setChartHeight] = useState(300);

  // Dynamically calculate chart height based on container size
  useEffect(() => {
    const calculateHeight = () => {
      if (chartContainerRef.current) {
        const containerHeight = chartContainerRef.current.offsetHeight;
        // Use container height with some padding buffer, minimum 200px for usability
        setChartHeight(Math.max(containerHeight - 20, 200));
      }
    };

    calculateHeight();
    
    // Recalculate on window resize
    window.addEventListener('resize', calculateHeight);
    
    // Use ResizeObserver for more accurate container size tracking
    const resizeObserver = new ResizeObserver(calculateHeight);
    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => {
      window.removeEventListener('resize', calculateHeight);
      resizeObserver.disconnect();
    };
  }, []);

  // Memoize chart data transformation - only recalculate when dataPoints change
  const allChartData = useMemo(() => {
    if (!data?.dataPoints) return [];
    return data.dataPoints.map(point => ({
      x: new Date(point.time).getTime(),
      y: point.close,
      originalData: point
    }));
  }, [data?.dataPoints]);

  // Memoize series data creation - only recalculate when chart data or annotations change
  const seriesData = useMemo(() => {
    if (!data?.xAxisAnnotations || data.xAxisAnnotations.length === 0) {
      return [{
        name: 'Price',
        data: allChartData,
        color: '#000000'
      }];
    }

    const annotation = data.xAxisAnnotations[0];
    const startTime = new Date(annotation.startTime).getTime();
    const endTime = new Date(annotation.endTime).getTime();

    // Base series (black) - full data
    const baseSeries = {
      name: 'Price',
      data: allChartData,
      color: '#000000'
    };

    // Highlighted series (annotation color) - only data in annotation range
    const highlightedData = allChartData.filter(point => 
      point.x >= startTime && point.x <= endTime
    );

    const highlightedSeries = {
      name: 'Dip Period',
      data: highlightedData,
      color: annotation.style.lineColor
    };

    return [baseSeries, highlightedSeries];
  }, [allChartData, data?.xAxisAnnotations]);

  // Memoize annotations processing - only recalculate when annotations change
  const annotations = useMemo(() => {
    return {
      xaxis: data?.xAxisAnnotations ? data.xAxisAnnotations.map(annotation => ({
        x: new Date(annotation.startTime).getTime(),
        x2: new Date(annotation.endTime).getTime(),
        strokeDashArray: 0,
        borderColor: 'transparent',
        fillColor: annotation.style.lineColor,
        opacity: 0.05,
        label: {
          text: `${annotation.metadata.dipPercentage}% Dip`,
          position: 'bottom',
          offsetY: -10,
          style: {
            color: annotation.style.lineColor,
            background: 'rgba(255, 255, 255, 0.8)',
            fontSize: '12px',
            fontWeight: 'bold',
            padding: {
              left: 8,
              right: 8,
              top: 4,
              bottom: 4
            }
          }
        }
      })) : []
    };
  }, [data?.xAxisAnnotations]);

  // Memoize chart options - only recalculate when dependencies change
  const options = useMemo(() => {
    return {
      chart: {
        type: 'line',
        height: chartHeight,
        toolbar: {
          show: false
        },
        zoom: {
          enabled: true,
          type: 'x'
        },
        redrawOnParentResize: true,
        redrawOnWindowResize: true
      },
      xaxis: {
        type: 'datetime',
        labels: {
          format: 'dd MMM'
        }
      },
      yaxis: {
        tooltip: {
          enabled: true
        },
        labels: {
          formatter: (val) => `₹${val.toFixed(2)}`
        }
      },
      tooltip: {
        shared: true,
        intersect: false,
        x: {
          format: 'dd MMM yyyy'
        },
        y: {
          formatter: (val, { seriesIndex, dataPointIndex, w }) => {
            // Get the original data point
            const point = seriesData[seriesIndex]?.data[dataPointIndex]?.originalData;
            
            if (!point) return `₹${val.toFixed(2)}`;
            
            return `
              <div>
                <div>₹${val.toFixed(2)}</div>
              </div>
            `;
          }
        }
      },
      stroke: {
        curve: 'smooth',
        width: [2, 3],
        colors: seriesData.map(series => series.color)
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.1,
          opacityFrom: 0.4,
          opacityTo: 0.1,
          stops: [0, 100]
        }
      },
      markers: {
        size: [2, 4],
        colors: seriesData.map(series => series.color),
        strokeColors: '#fff',
        strokeWidth: 2,
        hover: {
          size: 5
        }
      },
      legend: {
        show: false,
        position: 'top',
        horizontalAlign: 'right'
      },
      grid: {
        show: true,
        borderColor: '#e0e0e0',
        strokeDashArray: 3,
        padding: {
          bottom: 15
        }
      },
      annotations: annotations
    };
  }, [seriesData, annotations, chartHeight]);

  // Memoize series data for ApexCharts - only recalculate when seriesData changes
  const series = useMemo(() => {
    return seriesData.map(seriesItem => ({
      name: seriesItem.name,
      data: seriesItem.data.map(point => ({
        x: point.x,
        y: point.y
      }))
    }));
  }, [seriesData]);

  // Early return for no data case
  if (!data || !data.dataPoints) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100">
        <div className="text-center">
          <div className="text-warning mb-3">
            <i className="fas fa-exclamation-triangle fa-3x"></i>
          </div>
          <h5 className="text-warning mb-3">No Data Available</h5>
          <p className="mb-3">
            {!data ? 'No data received from API' : 'Missing dataPoints in API response'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 rounded shadow-lg h-100 d-flex flex-column" style={{ overflow: 'hidden' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          <div className="d-flex align-items-center">
            <img 
              src={logoSrc} 
              alt={`${data.stock?.stockLabel} logo`}
              style={{ width: '40px', height: '40px', objectFit: 'contain' }}
            />
          </div>
          <span className="badge bg-success px-3 py-2" style={{ fontSize: '14px', fontWeight: '600' }}>
            {data.stock?.stockLabel}
          </span>
          <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: '20px', lineHeight: '1.2' }}>
            {data.stock?.name}
          </h4>
        </div>
      </div>
      
      <div className="row mb-4">
        <div className="col-md-4">
          <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded-3 border" style={{ height: '60px' }}>
            <div className="text-muted small fw-medium">LTP</div>
            <div className="fw-bold text-success" style={{ fontSize: '18px' }}>
              ₹{data.stock?.lastTradedPrice?.toLocaleString('en-IN')}
            </div>
          </div>
        </div>
        
        {data.xAxisAnnotations && data.xAxisAnnotations.length > 0 && (
          <>
            <div className="col-md-4">
              <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded-3 border" style={{ height: '60px' }}>
                <div>
                  <div className="text-muted small fw-medium">Peak Price</div>
                  <div className="text-muted small">
                    {data.xAxisAnnotations[0].startTime && (
                      <span>{new Date(data.xAxisAnnotations[0].startTime).toLocaleDateString('en-IN', { 
                        day: '2-digit', 
                        month: 'short' 
                      })}</span>
                    )}
                  </div>
                </div>
                <div className="fw-bold text-primary" style={{ fontSize: '18px' }}>
                  ₹{data.xAxisAnnotations[0].metadata.peakPrice?.toLocaleString('en-IN')}
                </div>
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded-3 border" style={{ height: '60px' }}>
                <div>
                  <div className="text-muted small fw-medium">Price Dip</div>
                  <div className="text-muted small">
                    {data.xAxisAnnotations[0].metadata.daysFromPeak} days
                  </div>
                </div>
                <div className="fw-bold text-warning" style={{ fontSize: '18px' }}>
                  {data.xAxisAnnotations[0].metadata.dipPercentage}%
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      <div 
        ref={chartContainerRef}
        className="flex-grow-1" 
        style={{ minHeight: 0, overflow: 'hidden' }}
      >
        <Chart
          options={options}
          series={series}
          type="line"
          height={chartHeight}
        />
      </div>
    </div>
  );
};

export default DipGalleryChart;
